import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import { Layout, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { PlayerProvider } from '@context/PlayerContext';
import MediaViewerHeader from '@components/MediaViewerHeader';
import {
  removeComment,
  editComment,
  setCommentLike,
  setAsset,
  setAssetVariant,
  setCommentResolve
} from '@redux/actions/mediaViewerAction';
import {
  CreateAWCommentDto,
  ReplyAWCommentDto,
  ApprovalWorkflowItemDto,
  GetAssetVersionCommentsSuccessDto,
  GetAssetVersionTasksLastReadSuccessDto
} from '@api/Api';
import { useApprovalPassword } from '@context/ApprovalPasswordContext';
import { useTypedSelector } from '@hooks';
import { nonNull } from '@helpers/non-null';
import {
  useWebSocketDebouncedSubscription,
  useWebSocketSubscription
} from '@hooks/useWebSocketClient';
import {
  approvalWorkflowAddNotificationSubscription,
  assetAwCommentsMarkAssetCommentsRead,
  assetAwCommentsCreateAssetComment,
  assetAwCommentsDeleteAssetComment,
  assetAwCommentsGetAssetComments,
  assetAwCommentsListCommentPotentialMentions,
  assetAwCommentsReplyAssetComment,
  assetAwCommentsSetAssetCommentLike,
  assetAwCommentsSetAssetCommentResolve,
  assetAwCommentsUpdateAssetComment
} from '@api/Approval';
import { ApprovalReviewerPage } from '@pages/ApprovalReviewer/ApprovalReviewer';
import AccessClose from '@pages/ExternalReviewer/AccessClose';
import { toggleApprovalModal } from '@redux/actions/modalAction';
import { assetSetRecentlyOpened } from '@api/Asset';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { apiClient } from '@api/client';
import {
  QUERY_APPROVAL_COMMENTS_ASSETS,
  useAwCommentsAssetsQuery
} from '@hooks/queries/approval';
import {
  QUERY_TASKS_LAST_READ,
  useAssetVersionTasksLastReadQuery
} from '@hooks/queries/task';
import { taskMarkAssetVersionTasksRead } from '@api/Task';
import { useTaskWebSocketSubscription } from '@hooks/tasks';
import { MediaViewerOutletContextState } from '@layouts/types';
import { useLayoutEffectWithPrev } from '@hooks/useEffectWithPrev';
import { useAssetByAwQuery } from '@hooks/queries/assets';
import { showNewAssetVersionToast } from '@helpers/showNewAssetVersionToast';
import { MediaViewerActionTypes } from '@redux/types/mediaViewerType';
import { useResponsive, useResetMobileViewport } from '@hooks/useResponsive';
import { useThemeColor } from '@hooks/useThemeColor';
import MediaViewerTourBanner from '@components/MediaViewerTourBanner/MediaViewerTourBanner';
import { AssetsSliderProvider } from '@pages/MediaViewer/components/AssetsSlider/AssetsSliderProvider';

const { Header, Content } = Layout;

const VIEWPORT_MIN_HEIGHT = 'var(--viewport-min-height, 100vh)';

type ApprovalReviewerMediaLayoutProps = {
  layoutType: 'asset' | 'batch-assets-list';
  approvalWorkflow: ApprovalWorkflowItemDto;
  isOrgMember: boolean;
};

function ApprovalReviewerMediaLayout({
  layoutType,
  approvalWorkflow,
  isOrgMember
}: ApprovalReviewerMediaLayoutProps) {
  useResetMobileViewport();

  const { isLgWidth } = useResponsive();
  const isManager = approvalWorkflow.permissions.manage;
  const accessCloseReason = !isManager
    ? approvalWorkflow.isPaused
      ? 'paused'
      : approvalWorkflow.isFutureReview
      ? 'stage-not-started'
      : undefined
    : undefined;
  const backgroundColor = accessCloseReason
    ? '#fff'
    : isLgWidth
    ? '#1A1C1E'
    : '#000';

  useThemeColor(backgroundColor);

  if (accessCloseReason)
    return (
      <ApprovalReviewerPage
        element={<AccessClose type="approval" awReason={accessCloseReason} />}
      />
    );
  // No assets slider in the list (its header navigation is not shown without
  // it). Coming from the list, the slider state (e.g. its order) starts anew.
  if (layoutType === 'batch-assets-list') {
    return (
      <ApprovalReviewerBatchAssetsList
        approvalWorkflow={approvalWorkflow}
        isOrgMember={isOrgMember}
      />
    );
  }
  if (layoutType === 'asset') {
    return (
      <AssetsSliderProvider>
        <ApprovalReviewerAsset
          approvalWorkflow={approvalWorkflow}
          isOrgMember={isOrgMember}
        />
      </AssetsSliderProvider>
    );
  }
  return null;
}

function ApprovalReviewerAsset({
  approvalWorkflow,
  isOrgMember
}: Omit<ApprovalReviewerMediaLayoutProps, 'layoutType'>) {
  const dispatch = useTypedDispatch();
  const { isLgWidth } = useResponsive();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const assetIdParam = searchParams.get('assetId');
  const versionIdParam = searchParams.get('versionId');
  const { password } = useApprovalPassword();
  const assetQuery = useAssetByAwQuery(
    { assetId: assetIdParam ?? '', awId: approvalWorkflow.id, password },
    { enabled: !!assetIdParam }
  );

  const asset = assetQuery.data ?? null;
  const { id: awId } = approvalWorkflow;
  const assetId = asset?.id;
  const versionId = selectedVersion?.id;
  const backgroundColor = isLgWidth ? '#1A1C1E' : '#000';
  const hasAccessToAsset = approvalWorkflow.assets.some(
    (x) => x.id === assetId && x.hasAccess
  );

  const prevVersionId = useRef(versionId || null);

  const { data: commentsList, isLoading: isLoadingCommentsList } =
    useAwCommentsAssetsQuery(
      {
        assetVersionId: versionId || '',
        awId: awId || '',
        approvalWorkflowPassword: password
      },
      {
        keepPreviousData: versionId === prevVersionId.current,
        enabled: !!versionId && !!awId
      }
    );
  const commentsLastReadAt = commentsList?.lastReadAt ?? null;
  const commentsItems = commentsList?.comments ?? [];

  const { data: tasksLastReadData } = useAssetVersionTasksLastReadQuery(
    { assetVersionId: versionId || '' },
    { enabled: !!versionId && isOrgMember }
  );
  const tasksLastReadAt = tasksLastReadData?.lastReadAt ?? null;
  const latestTaskCreatedAt = tasksLastReadData?.latestCreatedAt ?? null;

  if (versionId !== prevVersionId.current && !isLoadingCommentsList) {
    prevVersionId.current = versionId || null;
  }

  useEffect(() => {
    (async () => {
      if (!hasAccessToAsset || !assetId || !versionId) return;
      await assetSetRecentlyOpened({
        assetId,
        assetVersionId: versionId
      });
    })();
  }, [hasAccessToAsset, assetId, versionId]);

  useEffect(() => {
    approvalWorkflowAddNotificationSubscription({
      id: awId,
      password
    });
  }, [awId, password]);

  const outletContext = useMemo<MediaViewerOutletContextState>(() => {
    const params = {
      awId,
      approvalWorkflowPassword: password
    };
    return {
      fetchCommentsList: async ({ assetVersionId }) => {
        const { data } = await assetAwCommentsGetAssetComments({
          assetVersionId,
          ...params
        });
        return data;
      },
      addNewComment: async (data, parentId) => {
        try {
          let newComment = null;
          if (parentId && parentId !== 'new') {
            const {
              data: { comment }
            } = await assetAwCommentsReplyAssetComment({
              ...(data as ReplyAWCommentDto),
              ...params
            });
            newComment = comment;
          } else {
            const {
              data: { comment }
            } = await assetAwCommentsCreateAssetComment({
              ...(data as CreateAWCommentDto),
              ...params
            });
            newComment = comment;
          }
          return newComment;
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to create comment. Please try again.',
            className: 'message-dark-modal'
          });
          throw error;
        }
      },
      removeComment: async (id) => {
        try {
          await removeComment(id);
          await assetAwCommentsDeleteAssetComment({
            id,
            ...params
          });
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to delete comment. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      editComment: async (data) => {
        try {
          const {
            data: { comment }
          } = await assetAwCommentsUpdateAssetComment({
            ...data,
            ...params
          });
          await editComment(comment);
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to update comment. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      setCommentLike: async (data, user) => {
        try {
          await setCommentLike(data, user);
          await assetAwCommentsSetAssetCommentLike({
            ...data,
            ...params
          });
        } catch (error: any) {
          message.error({
            content:
              error?.response?.data?.message ||
              'Failed to update like status. Please try again.',
            className: 'message-dark-modal'
          });
        }
      },
      setCommentResolve: async (data, user) => {
        await setCommentResolve(data, user);
        await assetAwCommentsSetAssetCommentResolve({
          ...data,
          ...params
        });
      },
      getPotentialMentions: async ({ assetVersionId, searchQuery }) => {
        const {
          data: { edges }
        } = await assetAwCommentsListCommentPotentialMentions({
          assetVersionId,
          searchQuery,
          ...params
        });
        if (edges) {
          const list = edges.map((item) => item.node);
          return list;
        }
        return [];
      },
      getPotentialGroupMentions: async ({ assetVersionId, searchQuery }) => {
        const {
          data: { edges }
        } =
          await apiClient.approvalWorkflow.assetAwCommentsControllerListCommentPotentialGroupMentions(
            {
              assetVersionId,
              searchQuery,
              ...params
            }
          );
        if (edges) {
          const list = edges.map((item) => item.node);
          return list;
        }
        return [];
      },
      isLoadingCommentsList,
      commentsList: commentsItems,
      commentsLastReadAt,
      markCommentsRead: async () => {
        if (!versionId || !awId) return;
        const optimisticLastReadAt = new Date().toISOString();
        queryClient.setQueriesData(
          { queryKey: [QUERY_APPROVAL_COMMENTS_ASSETS, versionId] },
          (prev: GetAssetVersionCommentsSuccessDto | undefined) => {
            if (!prev) return prev;
            return {
              ...prev,
              lastReadAt:
                !prev.lastReadAt ||
                new Date(prev.lastReadAt) < new Date(optimisticLastReadAt)
                  ? optimisticLastReadAt
                  : prev.lastReadAt
            };
          }
        );

        const { data } = await assetAwCommentsMarkAssetCommentsRead({
          assetVersionId: versionId,
          awId,
          approvalWorkflowPassword: password
        });

        queryClient.setQueriesData(
          { queryKey: [QUERY_APPROVAL_COMMENTS_ASSETS, versionId] },
          (prev: GetAssetVersionCommentsSuccessDto | undefined) => {
            if (!prev) return prev;
            return {
              ...prev,
              lastReadAt:
                !prev.lastReadAt ||
                new Date(prev.lastReadAt) < new Date(data.lastReadAt)
                  ? data.lastReadAt
                  : prev.lastReadAt
            };
          }
        );
      },
      tasksLastReadAt,
      latestTaskCreatedAt,
      markTasksRead: async () => {
        if (!versionId || !isOrgMember) return;
        const optimisticLastReadAt = new Date().toISOString();
        queryClient.setQueriesData(
          { queryKey: [QUERY_TASKS_LAST_READ, versionId] },
          (prev: GetAssetVersionTasksLastReadSuccessDto | undefined) => {
            if (!prev) return prev;
            return {
              ...prev,
              lastReadAt:
                !prev.lastReadAt ||
                new Date(prev.lastReadAt) < new Date(optimisticLastReadAt)
                  ? optimisticLastReadAt
                  : prev.lastReadAt
            };
          }
        );

        const { data } = await taskMarkAssetVersionTasksRead({
          assetVersionId: versionId
        });

        queryClient.setQueriesData(
          { queryKey: [QUERY_TASKS_LAST_READ, versionId] },
          (prev: GetAssetVersionTasksLastReadSuccessDto | undefined) => {
            if (!prev) return prev;
            return {
              ...prev,
              lastReadAt:
                !prev.lastReadAt ||
                new Date(prev.lastReadAt) < new Date(data.lastReadAt)
                  ? data.lastReadAt
                  : prev.lastReadAt
            };
          }
        );
      }
    };
  }, [
    password,
    awId,
    versionId,
    isOrgMember,
    isLoadingCommentsList,
    commentsItems,
    commentsLastReadAt,
    tasksLastReadAt,
    latestTaskCreatedAt,
    queryClient
  ]);

  useLayoutEffect(() => {
    dispatch(toggleApprovalModal(null));
    dispatch({
      type: MediaViewerActionTypes.SET_RESET_ASSET_STATE
    });
  }, [assetIdParam, dispatch]);

  useLayoutEffectWithPrev(
    ([prevAsset, prevVersionId]) => {
      dispatch(setAsset(asset));
      const version =
        asset?.asset?.versions.find((v) => v.id === versionIdParam) ??
        asset?.asset?.versions[0];
      dispatch(setAssetVariant(version?.id ?? null));

      let newVersionAdded = false;
      let newVersionAddedByMe = false;
      if (prevAsset?.id === asset?.id) {
        const optimisticUpdate =
          !!(prevAsset as any)?.versionsOptimisticUpdate ||
          !!(asset as any)?.versionsOptimisticUpdate;
        const prevVersionIds = (prevAsset?.asset?.versions ?? []).map(
          (v) => v.id
        );
        const newVersionIds = (asset?.asset?.versions ?? []).map((v) => v.id);
        newVersionAdded =
          !optimisticUpdate &&
          newVersionIds[0] !== prevVersionId &&
          newVersionIds.some((id) => !prevVersionIds.includes(id));
        if (newVersionAdded) {
          const newVersion = asset?.asset?.versions.find(
            (v) => !prevVersionIds.includes(v.id)
          );
          newVersionAddedByMe = !!newVersion?.user?.me;
        }
      }

      if (asset && version?.id !== versionIdParam) {
        const version = asset?.asset?.versions[0];
        setSearchParams(
          (prev) => {
            prev.set('versionId', version?.id ?? '');
            return prev;
          },
          { replace: true }
        );
      } else if (newVersionAdded) {
        const goToLatestVersion = () =>
          setSearchParams((prev) => {
            prev.delete('versionId');
            return prev;
          });
        if (newVersionAddedByMe) goToLatestVersion();
        else showNewAssetVersionToast(nonNull(assetId), goToLatestVersion);
      }
    },
    [asset, versionIdParam, dispatch]
  );

  const assetIdRef = useRef(assetId);
  assetIdRef.current = assetId;

  const versionIdRef = useRef(selectedVersion?.id);
  versionIdRef.current = selectedVersion?.id;

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.assetId !== assetIdRef.current) return false;
    if (msg.payload.assetVersionId !== versionIdRef.current) return false;
    if (msg.causedByMe) return;
    await queryClient.invalidateQueries({
      queryKey: [QUERY_APPROVAL_COMMENTS_ASSETS, versionIdRef.current]
    });
  }, 'asset-comment-created');

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.assetId !== assetIdRef.current) return false;
    if (msg.payload.assetVersionId !== versionIdRef.current) return false;
    if (msg.causedByMe) return;
    await queryClient.invalidateQueries({
      queryKey: [QUERY_APPROVAL_COMMENTS_ASSETS, versionIdRef.current]
    });
  }, 'asset-comment-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.target.assetId,
    async (messages) => {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.payload.target.assetId !== assetIdRef.current) return;
      await queryClient.invalidateQueries({
        queryKey: [QUERY_APPROVAL_COMMENTS_ASSETS, versionIdRef.current]
      });
    },
    600,
    (msg) => {
      if (msg.payload.target.assetId !== assetIdRef.current) return false;
      if (!msg.payload.target.commentId) return false;
      return true;
    },
    'attachment-created'
  );

  useWebSocketSubscription(async (msg) => {
    if (msg.payload.target.assetId !== assetIdRef.current) return false;
    if (msg.causedByMe) return;
    await queryClient.invalidateQueries({
      queryKey: [QUERY_APPROVAL_COMMENTS_ASSETS, versionIdRef.current]
    });
  }, 'attachment-removed');

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.assetId,
    async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_APPROVAL_COMMENTS_ASSETS, versionIdRef.current]
      });
    },
    1000,
    (msg) => {
      if (msg.payload.assetId !== assetIdRef.current) return false;
      if (msg.payload.assetVersionId !== versionIdRef.current) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'asset-comment-updated'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.assetId,
    () => assetQuery.refetch(),
    1000,
    (msg) => {
      if (msg.payload.assetId !== assetId) return false;
      if (msg.payload.reason === 'transcription-completed') return true;
      if (msg.payload.reason === 'new-version') return true;
      if (msg.payload.reason === 'conversion-status-changed') return true;
      return !msg.causedByMe;
    },
    'asset-updated'
  );

  useTaskWebSocketSubscription({
    workspaceId: isOrgMember ? asset?.workspaceId : undefined
  });

  // Rendered while the asset loads too (the viewer shows its loader): not
  // unmounted when switching to an asset that is not loaded yet, which would
  // reset the viewer (e.g. reopen the comments panel with its animation)
  const viewportMinHeight = `calc(${VIEWPORT_MIN_HEIGHT} - var(--demo-mode-panel-width, 0px))`;

  return (
    <>
      <MediaViewerTourBanner />
      <Layout
        style={{
          background: backgroundColor,
          color: '#fff',
          minHeight: viewportMinHeight
        }}
      >
        <Layout
          className="site-layout external-reviewer-media"
          style={{
            background: backgroundColor
          }}
        >
          <Header
            className="site-layout-background b-background-black"
            style={{
              padding: 0,
              width: '100%'
            }}
          >
            <MediaViewerHeader
              view={isOrgMember ? 'approval-asset' : 'external-approval-asset'}
              password={password}
              approvalWorkflowId={awId}
            />
          </Header>
          <Content style={{ marginTop: 70 }}>
            <PlayerProvider>
              <Outlet context={outletContext} />
            </PlayerProvider>
          </Content>
        </Layout>
      </Layout>
    </>
  );
}

function ApprovalReviewerBatchAssetsList({
  approvalWorkflow,
  isOrgMember
}: Omit<ApprovalReviewerMediaLayoutProps, 'layoutType'>) {
  const { password } = useApprovalPassword();
  const { isLgWidth } = useResponsive();
  const backgroundColor = isLgWidth ? '#1A1C1E' : '#000';
  return (
    <Layout
      style={{
        background: backgroundColor,
        color: '#fff',
        minHeight: VIEWPORT_MIN_HEIGHT
      }}
    >
      <Layout
        className="site-layout external-reviewer-media"
        style={{
          background: backgroundColor
        }}
      >
        <Header
          className="site-layout-background b-background-black"
          style={{
            padding: 0,
            width: '100%'
          }}
        >
          <MediaViewerHeader
            view={
              isOrgMember
                ? 'approval-assets-list'
                : 'external-approval-assets-list'
            }
            approvalWorkflowId={approvalWorkflow.id}
            password={password}
          />
        </Header>
        <Content style={{ marginTop: 70 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}

export default ApprovalReviewerMediaLayout;
